/**
 * The address bar, as the public form reads it (P1 / prefill playbook A2, A5).
 *
 * Four query parameters are the page's own — `invite`, `sso_error`,
 * `paid_session`, `payment` — and are stripped once read. Everything else
 * stays: a prefill link must survive a refresh, and a signed link is meant
 * to be re-openable. Tracking params (`utm_*`, `ref`, `src`, `campaign`) are
 * captured once at arrival and sent with the submission, never as answers.
 */
import rules from '../../../shared/prefillRules.json';
import type { FormState } from './model';
import { setValue } from './model';

/** The params the public page reads for itself and removes from the address bar. */
export const PAGE_PARAMS = ['invite', 'sso_error', 'paid_session', 'payment', 'edit', 'resume'];

const TRACKING_RE = new RegExp(rules.trackingPattern);

/** `search` without the named params — '' or '?a=b', ready to append to a pathname. */
export function withoutParams(search: string, names: string[]): string {
  let q: URLSearchParams;
  try { q = new URLSearchParams(search); } catch { return ''; }
  for (const n of names) q.delete(n);
  const s = q.toString();
  return s ? `?${s}` : '';
}

/** What the public GET is handed: the address bar's query minus the page's own params. */
export function forwardableSearch(search: string): string {
  return withoutParams(search, PAGE_PARAMS).replace(/^\?/, '');
}

/** The tracking params in `search`, trimmed and capped as the server keeps them. */
export function trackingParams(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  let q: URLSearchParams;
  try { q = new URLSearchParams(search); } catch { return out; }
  for (const [k, v] of q) {
    if (Object.keys(out).length >= rules.maxTracking) break;
    if (!TRACKING_RE.test(k) || k in out) continue;
    const text = v.trim().slice(0, rules.maxTrackingChars);
    if (text) out[k] = text;
  }
  return out;
}

/** Remove the named params from the address bar, keeping the rest. */
export function stripFromAddressBar(names: string[]): void {
  try {
    window.history.replaceState({}, '', window.location.pathname + withoutParams(window.location.search, names));
  } catch { /* ignore */ }
}

/**
 * Apply a prefill map to the order scope. A composite part (`addr.city`)
 * merges into its parent object; `skip` names the keys to leave alone (the
 * soft ones, when a draft is being restored over them).
 */
export function applyPrefill(state: FormState, prefill: Record<string, unknown> | undefined, skip?: Set<string>): FormState {
  let next = state;
  for (const [key, value] of Object.entries(prefill || {})) {
    if (skip?.has(key)) continue;
    const dot = key.indexOf('.');
    if (dot < 0) { next = setValue(next, [], key, value); continue; }
    const parent = key.slice(0, dot);
    const cur = next.order[parent];
    const base = cur && typeof cur === 'object' && !Array.isArray(cur) ? (cur as Record<string, unknown>) : {};
    next = setValue(next, [], parent, { ...base, [key.slice(dot + 1)]: value });
  }
  return next;
}
